/**
 * Composer (input + Enviar + Agendar) do chat.
 *
 * - Backend monta prefixo `*Nome*\n` automaticamente. Frontend so manda
 *   `raw_text`. Counter 4096 chars.
 * - Enter = enviar, Shift+Enter = quebra linha.
 * - Agendar = SlideOverPanel com datetime-local (min=now+1min, max=+30d).
 * - Disabled quando: conversa nao-aberta, instancia pausada (kill switch
 *   ja bloqueia no backend; frontend so reflete UX). Hint mostra motivo.
 */
import React, { useMemo, useRef, useState } from "react";
import { FiClock, FiSend } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { criarMensagem } from "../../../services/whatsapp";
import {
  ErrorMsg,
  Field,
  FieldLabel,
  Form,
  FormFooter,
  InlineHint,
  Input,
  SecondaryButton,
  Textarea,
} from "../../Orcamentos/styles";
import {
  ComposerActions,
  ComposerCounter,
  ComposerHint,
  ComposerInputRow,
  ComposerWrap,
} from "../styles";

const LIMITE_CHARS = 4096;

function isoLocalDatetime(date) {
  // Formato YYYY-MM-DDTHH:mm para input type=datetime-local
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

export default function Composer({ conversa, onEnviado, disabled }) {
  const { request } = useAuth();
  const [texto, setTexto] = useState("");
  const [agendarOpen, setAgendarOpen] = useState(false);
  const [agendarData, setAgendarData] = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 5);
    return isoLocalDatetime(d);
  });
  const [agendarErro, setAgendarErro] = useState(null);
  const textareaRef = useRef(null);

  const hint = useMemo(() => {
    if (disabled) {
      if (conversa?.status && conversa.status !== "ABERTA") {
        return `Conversa está ${conversa.status.toLowerCase()}. Reabra para enviar.`;
      }
      return "Envio indisponível agora.";
    }
    if (conversa?.instance_envio_pausado) {
      return "Instancia pausada (kill switch). Envio bloqueado pelo gestor.";
    }
    if (conversa?.instance_status && conversa.instance_status !== "CONECTADA") {
      return `Instância está ${conversa.instance_status}. Reconecte para enviar.`;
    }
    return null;
  }, [conversa, disabled]);

  const enviarImediato = async () => {
    const limpo = texto.trim();
    if (!limpo) throw new Error("Mensagem vazia.");
    if (limpo.length > LIMITE_CHARS)
      throw new Error(`Texto excede ${LIMITE_CHARS} caracteres.`);
    const msg = await criarMensagem(request, conversa.id, { raw_text: limpo });
    setTexto("");
    if (onEnviado) onEnviado(msg);
  };

  const agendar = async () => {
    setAgendarErro(null);
    const limpo = texto.trim();
    if (!limpo) {
      setAgendarErro("Texto vazio.");
      throw new Error("Texto vazio.");
    }
    if (!agendarData) {
      setAgendarErro("Escolha data e hora.");
      throw new Error("Data inválida.");
    }
    const dt = new Date(agendarData);
    if (Number.isNaN(dt.getTime())) {
      setAgendarErro("Data inválida.");
      throw new Error("Data inválida.");
    }
    if (dt.getTime() < Date.now() + 60000) {
      setAgendarErro("Agendamento deve ser pelo menos 1 minuto no futuro.");
      throw new Error("Data muito próxima.");
    }
    const iso = dt.toISOString();
    const msg = await criarMensagem(request, conversa.id, {
      raw_text: limpo,
      scheduled_at: iso,
    });
    setTexto("");
    setAgendarOpen(false);
    if (onEnviado) onEnviado(msg);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && texto.trim()) {
        enviarImediato().catch(() => {});
      }
    }
  };

  const minDateTime = (() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 1);
    return isoLocalDatetime(d);
  })();
  const maxDateTime = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return isoLocalDatetime(d);
  })();

  return (
    <ComposerWrap>
      {hint && <ComposerHint>{hint}</ComposerHint>}
      <ComposerInputRow>
        <textarea
          ref={textareaRef}
          rows={2}
          value={texto}
          onChange={(e) => setTexto(e.target.value.slice(0, LIMITE_CHARS))}
          onKeyDown={handleKey}
          placeholder={
            disabled
              ? "Envio indisponível"
              : "Digite uma mensagem... (Enter envia, Shift+Enter = nova linha)"
          }
          disabled={disabled || !conversa}
        />
        <ComposerActions>
          <AsyncButton
            variant="primary"
            onClick={enviarImediato}
            disabled={disabled || !conversa || !texto.trim()}
            errorMessage="Erro ao enviar"
          >
            <FiSend style={{ marginRight: 4 }} /> Enviar
          </AsyncButton>
          <SecondaryButton
            type="button"
            disabled={disabled || !conversa || !texto.trim()}
            onClick={() => setAgendarOpen(true)}
          >
            <FiClock style={{ marginRight: 4 }} /> Agendar
          </SecondaryButton>
        </ComposerActions>
      </ComposerInputRow>
      <ComposerCounter $warning={texto.length > LIMITE_CHARS - 100}>
        {texto.length}/{LIMITE_CHARS}
      </ComposerCounter>

      <SlideOverPanel
        open={agendarOpen}
        onClose={() => {
          setAgendarOpen(false);
          setAgendarErro(null);
        }}
        title="Agendar mensagem"
        subtitle="Escolha quando enviar. Worker cron dispara automaticamente."
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            agendar().catch(() => {});
          }}
        >
          <Field>
            <FieldLabel>Quando enviar</FieldLabel>
            <Input
              type="datetime-local"
              value={agendarData}
              min={minDateTime}
              max={maxDateTime}
              onChange={(e) => setAgendarData(e.target.value)}
              required
            />
            <InlineHint>
              Pelo menos 1 minuto no futuro. Máximo +30 dias.
            </InlineHint>
          </Field>
          <Field>
            <FieldLabel>Preview do texto</FieldLabel>
            <Textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value.slice(0, LIMITE_CHARS))}
              rows={4}
              placeholder="Digite o texto da mensagem"
            />
            <InlineHint>
              O prefixo *Nome* será adicionado automaticamente ao enviar.
            </InlineHint>
          </Field>

          {agendarErro && <ErrorMsg>{agendarErro}</ErrorMsg>}

          <FormFooter>
            <SecondaryButton
              type="button"
              onClick={() => setAgendarOpen(false)}
            >
              Cancelar
            </SecondaryButton>
            <AsyncButton
              variant="primary"
              onClick={agendar}
              successMessage="Mensagem agendada"
              silentError
            >
              <FiClock style={{ marginRight: 4 }} /> Agendar envio
            </AsyncButton>
          </FormFooter>
        </Form>
      </SlideOverPanel>
    </ComposerWrap>
  );
}
